import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Music, Sparkles, Loader2, Download, Plus, Save, Trash2, Cpu, Zap, Lock } from "lucide-react";
import { api, formatApiError, trackEvent } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { STYLE_PRESETS, presetById, generateProceduralMusic } from "../lib/musicGen";
import { audioBufferToWav } from "../lib/wav";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * AI Music Generator — local procedural (free) + fal.ai GPU (subscribers).
 * props: getCtx() -> AudioContext, onAddToMixer(name, buffer), isSub, user, onUpgrade
 */
export function AiMusicGenerator({ getCtx, onAddToMixer, isSub, user, onUpgrade }) {
  const { t } = useLang();
  const [prompt, setPrompt] = useState("");
  const [styleId, setStyleId] = useState("lofi");
  const [duration, setDuration] = useState(20);
  const [instrumental, setInstrumental] = useState(true);
  const [engine, setEngine] = useState("local");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {url, buffer?, blob?, tags?, name, engine, saved, track_id?}
  const [library, setLibrary] = useState([]);
  const objUrlRef = useRef(null);

  const maxDur = engine === "gpu" ? 120 : 60;

  const loadLibrary = useCallback(async () => {
    if (!user) { setLibrary([]); return; }
    try { const { data } = await api.get("/ai/tracks"); setLibrary(data.tracks); } catch (e) {}
  }, [user]);

  useEffect(() => { loadLibrary(); }, [loadLibrary]);
  useEffect(() => () => { if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current); }, []);
  useEffect(() => { if (duration > maxDur) setDuration(maxDur); }, [engine]); // eslint-disable-line

  const trackName = () => (prompt.trim().slice(0, 40) || presetById(styleId).label) + " · " + presetById(styleId).label;

  const generate = async () => {
    if (busy) return;
    if (engine === "gpu") {
      if (!isSub) { toast.error(t("gen.gpuGated")); onUpgrade?.(); return; }
      if (!prompt.trim()) { toast.error(t("gen.promptPh")); return; }
      setBusy(true);
      trackEvent("music_generate", "gpu");
      try {
        const { data } = await api.post("/ai/generate-music", {
          prompt: prompt.trim(), instrumental, duration, style: presetById(styleId).label,
        });
        if (objUrlRef.current) { URL.revokeObjectURL(objUrlRef.current); objUrlRef.current = null; }
        setResult({ url: `${BACKEND}${data.url}`, tags: data.tags, name: trackName(),
          engine: "gpu", saved: true, track_id: data.track_id });
        toast.success(t("gen.result"));
        loadLibrary();
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally { setBusy(false); }
    } else {
      // local procedural
      setBusy(true);
      trackEvent("music_generate", "local");
      try {
        const ctx = getCtx();
        await ctx.resume?.();
        const buffer = await generateProceduralMusic(ctx, { styleId, durationSec: duration, instrumental });
        const blob = audioBufferToWav(buffer, 16);
        if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current);
        const url = URL.createObjectURL(blob);
        objUrlRef.current = url;
        setResult({ url, buffer, blob, name: trackName(), engine: "local", saved: false, tags: presetById(styleId).label });
        toast.success(t("gen.result"));
      } catch (e) {
        toast.error("Erreur : " + e.message);
      } finally { setBusy(false); }
    }
  };

  const decodeUrl = async (url) => {
    const ctx = getCtx();
    const buf = await (await fetch(url)).arrayBuffer();
    return await ctx.decodeAudioData(buf);
  };

  const addToMixer = async (r) => {
    try {
      const buffer = r.buffer || (await decodeUrl(r.url));
      onAddToMixer?.(r.name || "Morceau IA", buffer);
      toast.success(t("gen.addToMixer") + " ✓");
    } catch (e) { toast.error("Erreur : " + e.message); }
  };

  const download = async (r) => {
    let blob = r.blob;
    if (!blob) blob = await (await fetch(r.url)).blob();
    const a = document.createElement("a");
    const u = URL.createObjectURL(blob);
    a.href = u; a.download = `${(r.name || "tubmix-ia").replace(/[^\w\-]+/g, "_")}.wav`; a.click();
    setTimeout(() => URL.revokeObjectURL(u), 1000);
  };

  const saveLocal = async () => {
    if (!result?.blob) return;
    if (!user) { toast.error(t("gen.loginToSave")); return; }
    try {
      const fd = new FormData();
      fd.append("file", result.blob, "tubmix-ia.wav");
      fd.append("prompt", prompt.trim());
      fd.append("style", presetById(styleId).label);
      fd.append("engine", "local");
      fd.append("duration", String(duration));
      await api.post("/ai/tracks/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult((r) => ({ ...r, saved: true }));
      toast.success(t("gen.saved"));
      loadLibrary();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteTrack = async (id) => {
    try { await api.delete(`/ai/tracks/${id}`); setLibrary((l) => l.filter((x) => x.track_id !== id)); toast.success(t("gen.deleted")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-gradient-to-br from-[#14141d] to-[#1c1c28] border border-[#2a2a3d] rounded-2xl p-4 sm:p-5 mb-5" data-testid="ai-music-generator">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-[#a855f7] to-[#4a6cf7] flex items-center justify-center"><Music className="h-5 w-5 text-white" /></div>
        <div>
          <div className="font-heading font-bold text-white">{t("gen.title")}</div>
          <div className="text-xs text-[#8888a8]">{t("gen.subtitle")}</div>
        </div>
      </div>

      {/* style presets */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="gen-styles">
        {STYLE_PRESETS.map((s) => (
          <button key={s.id} onClick={() => setStyleId(s.id)} data-testid={`gen-style-${s.id}`}
            className={`text-xs font-semibold rounded-full px-3 py-1.5 transition-colors ${styleId === s.id ? "bg-[#a855f7] text-white" : "bg-[#26263a] text-[#c9c9e0] hover:bg-[#33334d]"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} data-testid="gen-prompt"
        placeholder={t("gen.promptPh")}
        className="w-full resize-none bg-[#08080c] border border-[#2a2a3d] rounded-xl px-3.5 py-2.5 text-sm text-[#f0f0f8] outline-none focus:border-[#a855f7] transition-colors mb-3" />

      {/* controls */}
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div>
          <div className="flex justify-between text-[11px] font-mono uppercase text-[#8888a8] mb-1"><span>{t("gen.durationLabel")}</span><span>{duration}s</span></div>
          <input type="range" min={5} max={maxDur} step={5} value={duration} data-testid="gen-duration"
            onChange={(e) => setDuration(parseInt(e.target.value))} className="w-full accent-[#a855f7]" />
        </div>
        <div className="flex items-end gap-2">
          <button onClick={() => setInstrumental((v) => !v)} data-testid="gen-instrumental"
            className={`flex-1 rounded-xl py-2.5 text-sm font-semibold transition-colors ${instrumental ? "bg-[#a855f7] text-white" : "bg-[#26263a] text-[#c9c9e0] hover:bg-[#33334d]"}`}>
            {t("gen.instrumental")}
          </button>
        </div>
      </div>

      {/* engine selector */}
      <div className="grid grid-cols-2 gap-2 mb-3" data-testid="gen-engine">
        <button onClick={() => setEngine("local")} data-testid="gen-engine-local"
          className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold transition-colors ${engine === "local" ? "bg-[#10d982] text-black" : "bg-[#26263a] text-[#c9c9e0] hover:bg-[#33334d]"}`}>
          <Cpu className="h-4 w-4" /> {t("gen.engineLocal")}
        </button>
        <button onClick={() => setEngine("gpu")} data-testid="gen-engine-gpu"
          className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold transition-colors ${engine === "gpu" ? "bg-gradient-to-r from-[#ff6b35] to-[#a855f7] text-white" : "bg-[#26263a] text-[#c9c9e0] hover:bg-[#33334d]"}`}>
          {!isSub ? <Lock className="h-4 w-4" /> : <Zap className="h-4 w-4" />} {t("gen.engineGpu")}
        </button>
      </div>

      <button onClick={generate} disabled={busy} data-testid="gen-generate-btn"
        className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#a855f7] to-[#4a6cf7] hover:opacity-90 disabled:opacity-50 rounded-xl py-3 font-semibold text-white transition-opacity">
        {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> {engine === "gpu" ? t("gen.generating") : t("gen.localGenerating")}</> : <><Sparkles className="h-4 w-4" /> {t("gen.generate")}</>}
      </button>

      {/* result */}
      {result && (
        <div className="mt-4 bg-[#08080c] border border-[#2a2a3d] rounded-xl p-3.5" data-testid="gen-result">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="min-w-0">
              <div className="font-semibold text-sm text-white truncate">{result.name}</div>
              {result.tags && <div className="text-[11px] text-[#8888a8] truncate">{t("gen.tags")}: {result.tags}</div>}
            </div>
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${result.engine === "gpu" ? "bg-[#ff6b35]/20 text-[#ff6b35]" : "bg-[#10d982]/20 text-[#10d982]"}`}>{result.engine === "gpu" ? "GPU" : "LOCAL"}</span>
          </div>
          <audio controls src={result.url} className="w-full h-10 mb-3" data-testid="gen-audio" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => addToMixer(result)} data-testid="gen-add-mixer" className="inline-flex items-center gap-1.5 bg-[#ff6b35] hover:bg-[#e85a28] rounded-lg px-3 py-2 text-xs font-semibold text-white transition-colors"><Plus className="h-3.5 w-3.5" /> {t("gen.addToMixer")}</button>
            <button onClick={() => download(result)} data-testid="gen-download" className="inline-flex items-center gap-1.5 bg-[#26263a] hover:bg-[#33334d] rounded-lg px-3 py-2 text-xs font-semibold transition-colors"><Download className="h-3.5 w-3.5" /> {t("gen.download")}</button>
            {result.engine === "local" && (
              <button onClick={saveLocal} disabled={result.saved} data-testid="gen-save" className="inline-flex items-center gap-1.5 bg-[#26263a] hover:bg-[#33334d] disabled:opacity-50 rounded-lg px-3 py-2 text-xs font-semibold transition-colors"><Save className="h-3.5 w-3.5" /> {result.saved ? t("gen.saved") : t("gen.save")}</button>
            )}
          </div>
        </div>
      )}

      {/* library */}
      {user && (
        <div className="mt-4">
          <div className="text-xs font-mono uppercase tracking-widest text-[#8888a8] mb-2">{t("gen.library")} ({library.length})</div>
          <div className="space-y-2 max-h-72 overflow-auto tx-dark-scroll" data-testid="gen-library">
            {library.map((tr) => (
              <div key={tr.track_id} className="bg-[#08080c] border border-[#2a2a3d] rounded-xl p-3" data-testid={`gen-lib-${tr.track_id}`}>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{tr.prompt || tr.style || "Morceau IA"}</div>
                    <div className="text-[11px] text-[#8888a8] truncate">{tr.style} · {tr.duration}s · {tr.engine === "gpu" ? "GPU" : "Local"}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button data-testid={`gen-lib-add-${tr.track_id}`} onClick={() => addToMixer({ url: `${BACKEND}${tr.url}`, name: tr.prompt || tr.style })} className="h-7 w-7 rounded-lg bg-[#26263a] hover:bg-[#ff6b35] flex items-center justify-center transition-colors" title={t("gen.addToMixer")}><Plus className="h-3.5 w-3.5" /></button>
                    <button data-testid={`gen-lib-delete-${tr.track_id}`} onClick={() => deleteTrack(tr.track_id)} className="h-7 w-7 rounded-lg bg-[#26263a] hover:bg-[#ff3b5c] flex items-center justify-center transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <audio controls src={`${BACKEND}${tr.url}`} className="w-full h-9" />
              </div>
            ))}
            {!library.length && <p className="text-xs text-[#8888a8]">{t("gen.empty")}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
